// NEW_FILE_JUSTIFIED: LibraryStatsRail is a Step 1 sub-component extracted to keep CreateTypeStep1Page under 200 lines; no existing component covers this stats + recents sidebar pattern
import type { Id } from '@convex/_generated/dataModel';
import { useQuery } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is an npm package, not a local convex/ directory file
import { api } from '../../../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API lives outside src/; no @convex/ path alias in tsconfig — five levels up from src/components/insurance/types/Wizard/
import type { ILibraryStatsRailProps } from '@/@types/insuranceBuilder';
import { Skeleton } from '@/components/ui/skeleton';

type TRecentItem = {
  _id: Id<'orgInsuranceTypes'>;
  name: string;
  providerName: string | undefined;
  updatedAt: number;
}; // LOCAL_TYPE_JUSTIFIED: mirrors listLibraryStats returns.recent shape; scoped to this file

function formatRelativeTime(ts: number): string {
  const diffDays = Math.floor((Date.now() - ts) / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 30) return `${diffDays}d ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
}

const STAT_KEYS = ['activeTypeCount', 'providerCount', 'policyCount'] as const;
const STAT_LABELS = ['Types', 'Providers', 'Policies'] as const;

export function LibraryStatsRail({ orgId }: ILibraryStatsRailProps) {
  const stats = useQuery(api['insurance/types'].listLibraryStats, { orgId });
  const recent: TRecentItem[] = stats?.recent ?? [];
  const isLoading = stats === undefined;

  return (
    <aside className="space-y-4">
      <p className="text-sm font-semibold text-foreground">Your library</p>
      <div className="grid grid-cols-3 gap-2 text-center">
        {STAT_KEYS.map((key, i) => (
          <div key={key} className="rounded-lg border bg-card p-3">
            {isLoading ? (
              <Skeleton className="h-6 w-8 mx-auto mb-1" />
            ) : (
              <p className="text-lg font-bold text-foreground">{stats[key]}</p>
            )}
            <p className="text-xs text-muted-foreground">{STAT_LABELS[i]}</p>
          </div>
        ))}
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Recently created
        </p>
        {isLoading && (
          <div aria-busy="true" aria-label="Loading library stats">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full rounded-md" />
            ))}
          </div>
        )}
        {!isLoading && recent.length === 0 && (
          <p className="text-xs text-muted-foreground py-2">No types yet.</p>
        )}
        {!isLoading &&
          recent.map(r => (
            <div
              key={r._id}
              className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-muted"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{r.name}</p>
                {r.providerName && (
                  <p className="text-xs text-muted-foreground truncate">{r.providerName}</p>
                )}
              </div>
              <span className="ml-2 shrink-0 text-xs text-muted-foreground">
                {formatRelativeTime(r.updatedAt)}
              </span>
            </div>
          ))}
      </div>
    </aside>
  );
}
